import { getCell, formats, withCellStyles } from '../../utils';
import { Row, Rows } from '../../interfaces';
import { FollowOnPortfolioDTO } from 'src/app/domain/follow-on/component/dto/FollowOnPortfolioDTO';
import { FollowOnModelTotalDTO } from 'src/app/domain/follow-on/component/dto/FollowOnModelTotalDTO';
import { CellStyle } from 'xlsx-js-style';
import { CommitmentAllocationModelExcelExport } from 'src/app/domain/follow-on/component/commitment-allocation-model-table/commitment-allocation-model-table.component';
import { boldGrayBgLeft, boldLightGrayBgLeft, left } from 'src/app/util/excelStyles';

const constHeaders = ['Company', 'Investment', 'Current company value'];

/**
 * Prepare data for excel export by adding styles, etc.
 *
 * @param exportExcelData export data for all companies.
 * @param headers Headers of the table.
 * @returns complete data for excel - rows.
 */
export const getPreparedData = (commitmentAllocation: CommitmentAllocationModelExcelExport, headers: string[]): Rows => {
  const newData: Rows = [];
  newData.push([...constHeaders.concat([null]), ...headers.concat([null]), ...headers.concat([null]), ...headers]);

  const statuses = ['active', 'realized'];
  const is = {
    active: false,
    realized: false,
  };
  const totals = {
    active: null,
    realized: null,
  };
  const activeLen = commitmentAllocation.active?.models?.length ? 3 + commitmentAllocation.active?.models?.length : 3;
  activeSubTotalIndex = activeLen;
  const realizedLen = commitmentAllocation.realized.models?.length;
  realizedSubTotalIndex = activeLen === 3 ? activeLen + realizedLen : activeLen + realizedLen + 2;
  statuses.forEach((status) => {
    const models = commitmentAllocation[status].models;
    if (models.length) {
      newData.push([withCellStyles(status === 'active' ? 'Active' : 'Realized', leftBold)]);
      const rows = getManyRows(models, headers);
      newData.push(...rows);
      const total = getOneRow(commitmentAllocation[status].total, headers, true, status);
      newData.push(total);
      is[status] = true;
      totals[status] = commitmentAllocation[status].total;
    }
  });
  if (is.active && is.realized) {
    const grandTotal = getOneRowGrandTotal(totals.active, headers);
    newData.push(grandTotal);
  }

  return newData;
};


/**
 * Get data mapping.
 *
 * @param model FollowOnPortfolioDTO to map.
 */
/* eslint-disable @typescript-eslint/naming-convention */
const getNewMapping = (model: FollowOnPortfolioDTO, i: number): object => ({
  'Pre-money': getCell({ v: zeroToUndefined(model.rounds[i].preMoney), z: formats.thousandsSeparator, t: 'n' }),
  'Total round': getCell({ v: zeroToUndefined(model.rounds[i].totalRound), z: formats.thousandsSeparator, t: 'n' }),
  'Post-money': getCell({ v: zeroToUndefined(model.rounds[i].postMoney), z: formats.thousandsSeparator, t: 'n' }),
  'Exit value': getCell({ v: zeroToUndefined(model.rounds[i].exitValue), z: formats.thousandsSeparator, t: 'n' }),
  'Exit probability': getCell({ v: zeroToEmptyStr(model.rounds[i].exitProbability), z: formats.thousandsSeparator, t: 'n' }),
  'Potential multiple': getCell({ v: zeroToUndefined(model.rounds[i].multiple), z: formats.thousandsSeparator, t: 'n' }),
  'Investment allocation': getCell({ v: zeroToUndefined(model.rounds[i].cost), z: formats.thousandsSeparator, t: 'n' }),
  'empty': null,
});

const getConstDataMapping = (model: FollowOnPortfolioDTO): object => ({
  Company: model.name,
  Investment: getCell({ v: zeroToUndefined(model.currentCost), z: formats.thousandsSeparator, t: 'n' }),
  'Current company value': getCell({ v: zeroToUndefined(model.currentCompValue), z: formats.thousandsSeparator, t: 'n' }),
  'empty': null,
});

/**
 * Get total data mapping.
 *
 * @param model FollowOnModelTotalDTO to map.
 */
/* eslint-disable @typescript-eslint/naming-convention */
const getNewTotalMapping = (model: FollowOnModelTotalDTO, i: number, status: string, letter: string): object => ({
  'Pre-money': '',
  'Total round': getCell({ f: getSumFormulaTotal(letter, status),
    z: formats.thousandsSeparator,
    t: 'n' }),
  'Post-money': '',
  'Exit value': '',
  'Exit probability': '',
  'Potential multiple': '',
  'Investment allocation': '',
  'empty': null,
});

const getSumFormulaTotal = (letter: string, status: string): string => {
  if (status === 'grand') {
    return getSumForGRandTotal(letter);
  } if (status === 'active') {
    return `SUM(${letter}3:${letter}${activeSubTotalIndex - 1})`;
  }
  const realizedIndexStart = activeSubTotalIndex === 3 ? 3 : activeSubTotalIndex + 2;
  return `SUM(${letter}${realizedIndexStart}:${letter}${realizedSubTotalIndex - 1})`;

};

const getSumForGRandTotal = (letter: string): string => {
  return `SUM(${letter}${activeSubTotalIndex},${letter}${realizedSubTotalIndex})`;
};

const getConstDataTotalMapping = (model: FollowOnModelTotalDTO, status: string): object => ({
  Investment: getCell({ f: getSumFormulaTotal('B', status),
    z: formats.thousandsSeparator,
    t: 'n' }),
  'Current company value': '',
  'empty': null,
});

const getConstDataGrandTotalMapping = (): object => ({
  Investment: getCell({
    f: getSumFormulaTotal('B', 'grand'),
    z: formats.thousandsSeparator,
    t: 'n',
  }),
  'Current company value': '',
  'empty': null,
});


/**
 * Prepares rows for defined rounds.
 *
 * @param models FollowOnPortfolioDTO list.
 * @param headers Table headers.
 * @returns prepared rows for defined rounds.
 */
export const getManyRows = (models: FollowOnPortfolioDTO[], headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = [];

  models.forEach((model) => {
    const modelObj = {
      constData: getConstDataMapping(model),
      rounds: [getNewMapping(model, 0), getNewMapping(model, 1), getNewMapping(model, 2)],
    };
    mappedToHeaders.push(modelObj);
  });
  const headersForLogic = headers.concat(['empty']);
  const constHeadersForLogic = constHeaders.concat(['empty']);
  for (const model of mappedToHeaders) {
    const row = [];
    for (const header of constHeadersForLogic) {
      row.push(withCellStyles(model.constData[header], left));
    }
    for (const round of model.rounds) {
      for (const header of headersForLogic) {
        row.push(withCellStyles(round[header], left));
      }
    }
    rows.push(row);
  }

  return rows;
};

/**
 * Prepares row for defined round.
 *
 * @param total FollowOnModelTotalDTO.
 * @param headers Table headers.
 * @returns prepared row for defined round.
 */
export const getOneRow = (total: FollowOnModelTotalDTO, headers: string[], isActive: boolean, status: string): Row => {
  const totalObj = {
    constData: getConstDataTotalMapping(total, status),
    rounds: [getNewTotalMapping(total, 0, status, 'F'), getNewTotalMapping(total, 1, status, 'L'), getNewTotalMapping(total, 2, status, 'R')],
  };
  isActive = status === 'active';
  const row: Row = [withCellStyles(isActive ? 'Total active' : 'Total realized', boldLightGrayBgLeft)];
  const headersForLogic = headers.concat(['empty']);
  const constHeadersForLogic = constHeaders.concat(['empty']);
  for (const header of constHeadersForLogic) {
    if (header !== 'Company') {
      row.push(withCellStyles(totalObj.constData[header], boldLightGrayBgLeft));
    }
  }
  for (const round of totalObj.rounds) {
    for (const header of headersForLogic) {
      row.push(withCellStyles(round[header], boldLightGrayBgLeft));
    }
  }
  return row;
};

export const getOneRowGrandTotal = (active: FollowOnModelTotalDTO, headers: string[]): Row => {
  const totalObj = {
    constData: getConstDataGrandTotalMapping(),
    rounds: [
      getNewTotalMapping(active, 0, 'grand', 'F'),
      getNewTotalMapping(active, 1, 'grand', 'L'),
      getNewTotalMapping(active, 2, 'grand', 'R'),
    ],
  };
  const row: Row = [withCellStyles('Grand total', boldGrayBgLeft)];
  const headersForLogic = headers.concat(['empty']);
  const constHeadersForLogic = constHeaders.concat(['empty']);
  for (const header of constHeadersForLogic) {
    if (header !== 'Company') {
      row.push(withCellStyles(totalObj.constData[header], boldGrayBgLeft));
    }
  }
  for (const round of totalObj.rounds) {
    for (const header of headersForLogic) {
      row.push(withCellStyles(round[header], boldGrayBgLeft));
    }
  }
  return row;
};

function zeroToUndefined(v: number): string {
  if (v) {
    return v.toFixed(0);
  };
  return undefined;
}

function zeroToEmptyStr(v: number): string {
  if (v) {
    return v.toFixed(0);
  }
  return '';
}

const leftBold: CellStyle = {
  alignment: { horizontal: 'left' },
  font: { bold: true },
};

let activeSubTotalIndex: number;
let realizedSubTotalIndex: number;
